'use client';

import { createContext, FC, memo, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { getTimeStringFromServer } from '../utils/date';
import { formatDate } from '../utils/format';
type ClientTimeProps = {
  time?: string | null;
};
type ClientDateProps = {
  date?: Date | null;
  includeYear?: boolean;
};
type TimezoneView = 'event-time' | 'my-time';
type TimezoneContextType = {
  timezone?: string;
  view: TimezoneView | null;
  setView: (view: TimezoneView) => void;
};
type TimezoneProviderProps = {
  timezone?: string;
  view?: TimezoneView;
};
const TimezoneContext = createContext<TimezoneContextType>({} as TimezoneContextType);
export const ClientTime: FC<ClientTimeProps> = memo(({
  time
}) => {
  const {
    timezone,
    view
  } = useTimezoneContext();
  const [timeString, setTimeString] = useState<string>('');
  useEffect(() => {
    if (time) {
      setTimeString(getTimeStringFromServer(time, view === 'event-time' ? timezone : undefined));
    }
  }, [time, timezone, view]);
  return <span data-src={time ?? ''} data-tz={timezone} data-view={view} className="inline-block min-w-10" suppressHydrationWarning>
      {timeString}
    </span>;
});
export const ClientDate: FC<ClientDateProps> = memo(({
  date,
  includeYear = false
}) => {
  const {
    timezone,
    view
  } = useTimezoneContext();
  const [dateString, setDateString] = useState<string>('');
  useEffect(() => {
    if (date) {
      setDateString(formatDate(date, includeYear, view === 'event-time' ? timezone : undefined));
    }
  }, [date, timezone, view]);
  return <span data-src={date?.toISOString() ?? ''} data-tz={timezone} data-view={view} className="inline-block min-w-10" suppressHydrationWarning>
      {dateString}
    </span>;
});
export const useTimezoneContext = () => useContext(TimezoneContext) ?? {};
export const useTimezone = () => useTimezoneContext().timezone;
const localStorageKey = '@clippd/scoreboard/timezone/view';
export const TimezoneProvider: FC<PropsWithChildren<TimezoneProviderProps>> = ({
  timezone,
  children,
  view: viewOverride
}) => {
  const [view, _setView] = useState<TimezoneView | null>(() => {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(localStorageKey) as TimezoneView ?? 'my-time' : null;
  });
  const setView = useCallback((value: TimezoneView) => {
    _setView(value);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(localStorageKey, value);
    }
  }, []);
  return <TimezoneContext.Provider value={{
    timezone,
    view: viewOverride ?? view,
    setView
  }} data-sentry-element="unknown" data-sentry-component="TimezoneProvider" data-sentry-source-file="client-time.tsx">
      {children}
    </TimezoneContext.Provider>;
};